import { BusinessObjectBase } from './base';
import { attendanceSchema } from '@schemas/business-objects/attendance';
import type { Session } from './session';
import type { Student } from './student';
import type { TermTime } from './term-time';
import type { TermTutor } from './term-tutor';
import type {
  AbsenceType,
  AttendanceType,
  CalendarDate,
  Id,
} from '../base-types';

export interface AttendanceData {
  readonly id: Id;
  readonly date?: CalendarDate;
  readonly attendanceType?: AttendanceType;
  readonly absenceType?: AbsenceType;
  readonly isTemporaryAssignment?: boolean;
  readonly studentId: Id;
  readonly student?: Student;
  readonly termTutorId: Id;
  readonly termTutor?: TermTutor;
  readonly timeId: Id;
  readonly time?: TermTime;
  readonly sessions?: Session[];
}

export interface Attendance extends Readonly<AttendanceData> {}
type AttendanceCreateWithChildrenData = Partial<
  Omit<AttendanceData, 'sessions'>
> & { sessions?: Partial<Session>[] };

export class Attendance extends BusinessObjectBase {
  static readonly entityName = 'Attendance' as const;
  static readonly plural = 'attendances';
  static schema = attendanceSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Attendance[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Attendance(data));
  }

  static async findOne(filter?: any): Promise<Attendance | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Attendance(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Attendance> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Attendance(data);
  }

  static async create(data: Omit<AttendanceData, 'id'>): Promise<Attendance> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Attendance(result);
  }

  static async createMany(
    data: Omit<AttendanceData, 'id'>[],
  ): Promise<Attendance[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Attendance(item));
  }

  static async createWithChildren(
    data: AttendanceCreateWithChildrenData,
  ): Promise<Attendance> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Attendance(result);
  }

  static async createManyWithChildren(
    data: AttendanceCreateWithChildrenData[],
  ): Promise<Attendance[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Attendance(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<AttendanceData>,
  ): Promise<Attendance> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Attendance(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
